import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { lunarToSolar } from '../../utils/lunarUtils';
import { Drawer, DrawerBody, DrawerSection, DrawerFooter, Button } from '../../shared/ui';

const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-500';
const SELECT_CLS = 'w-full h-9 px-3 text-sm border border-stone-200 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-500';
const LABEL_CLS  = 'block text-xs font-medium text-stone-600 mb-1';

const INITIAL = {
  title: '', description: '',
  lunar_day: 1, lunar_month: 1, lunar_year: new Date().getFullYear(),
  leap_month: false, recurs_annually: false,
  priority: 'medium', is_public: false,
};

const LunarEventModal = ({ isOpen, onClose, onEventCreated }) => {
  const { t, i18n } = useTranslation('calendar');
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'vi-VN';
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const previewSolar = lunarToSolar(form.lunar_day, form.lunar_month, form.lunar_year, form.leap_month);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!previewSolar) {
      toast.error(t('lunarEventModal.toast.invalidDate'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/calendar/events/lunar', form);
      toast.success(t('lunarEventModal.toast.created'));
      onEventCreated?.(res.data);
      onClose();
      setForm(INITIAL);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('lunarEventModal.toast.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={t('lunarEventModal.createTitle')}
      subtitle={t('lunarEventModal.createSubtitle')}
      width={420}
    >
      <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
        <DrawerBody>
          <DrawerSection title={t('lunarEventModal.section.info')}>
            <div className="space-y-3">
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.title')} *</label>
                <input
                  type="text" name="title" required
                  value={form.title} onChange={set}
                  placeholder={t('lunarEventModal.field.titlePlaceholder')}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.description')}</label>
                <textarea
                  name="description" rows={3}
                  value={form.description} onChange={set}
                  placeholder={t('lunarEventModal.field.descriptionPlaceholder')}
                  className={`${INPUT_CLS} resize-y min-h-[72px]`}
                />
              </div>
            </div>
          </DrawerSection>

          <DrawerSection title={t('lunarEventModal.section.lunarDate')}>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.day')} *</label>
                <input
                  type="number" name="lunar_day" required
                  min={1} max={30}
                  value={form.lunar_day} onChange={set}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.month')} *</label>
                <input
                  type="number" name="lunar_month" required
                  min={1} max={12}
                  value={form.lunar_month} onChange={set}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.year')} *</label>
                <input
                  type="number" name="lunar_year" required
                  min={1900} max={2100}
                  value={form.lunar_year} onChange={set}
                  className={INPUT_CLS}
                />
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox" name="leap_month"
                  checked={form.leap_month} onChange={set}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                {t('lunarEventModal.field.leapMonth')}
              </label>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox" name="recurs_annually"
                  checked={form.recurs_annually} onChange={set}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                {t('lunarEventModal.field.recursAnnually')}
              </label>
            </div>

            {previewSolar ? (
              <div className="mt-3 px-3 py-2 bg-teal-50 border border-teal-100 rounded-lg text-sm text-teal-700">
                <span className="font-medium">{t('lunarEventModal.solarPreview')}</span>
                {previewSolar.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            ) : (
              <div className="mt-3 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                {t('lunarEventModal.invalidLunarDate')}
              </div>
            )}
          </DrawerSection>

          <DrawerSection title={t('lunarEventModal.section.settings')}>
            <div className="space-y-3">
              <div>
                <label className={LABEL_CLS}>{t('lunarEventModal.field.priority')}</label>
                <select name="priority" value={form.priority} onChange={set} className={SELECT_CLS}>
                  <option value="low">{t('create.priority.low')}</option>
                  <option value="medium">{t('create.priority.medium')}</option>
                  <option value="high">{t('create.priority.high')}</option>
                  <option value="urgent">{t('create.priority.urgent')}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox" name="is_public"
                  checked={form.is_public} onChange={set}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                {t('lunarEventModal.field.showToRenters')}
              </label>
            </div>
          </DrawerSection>
        </DrawerBody>

        <DrawerFooter>
          <Button kind="primary" type="submit" loading={loading}>
            {t('lunarEventModal.submit')}
          </Button>
          <Button kind="ghost" type="button" onClick={onClose}>{t('lunarEventModal.cancel')}</Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

export default LunarEventModal;
